import { produce } from 'immer'
import { v4 as uuid } from 'uuid'

export const ADD_MESSAGE = 'kodi/addMessage'
export const UPDATE_MESSAGE = 'kodi/updateMessage'
export const SET_LOADING = 'kodi/setLoading'
export const SET_RESTORING = 'kodi/setRestoring'
export const RESET = 'kodi/reset'
export const SET_SESSIONS = 'kodi/setSessions'
export const APPEND_SESSIONS = 'kodi/appendSessions'
export const PREPEND_SESSION = 'kodi/prependSession'
export const RENAME_SESSION = 'kodi/renameSession'
export const SET_SESSION_FAVORITE = 'kodi/setSessionFavorite'
export const REMOVE_SESSION = 'kodi/removeSession'
export const SET_READ_ONLY = 'kodi/setReadOnly'
export const SET_AWAITING_APPROVAL = 'kodi/setAwaitingApproval'
export const SET_PENDING_INTERACTIVE = 'kodi/setPendingInteractive'
export const SET_ACTIVE_SESSION_TITLE = 'kodi/setActiveSessionTitle'
export const SET_USAGE_POLICIES = 'kodi/setUsagePolicies'

export const addMessage = (payload) => ({ type: ADD_MESSAGE, payload })
export const updateMessage = (payload) => ({ type: UPDATE_MESSAGE, payload })
export const setLoading = (payload) => ({ type: SET_LOADING, payload })
export const setRestoring = (payload) => ({ type: SET_RESTORING, payload })
export const reset = () => ({ type: RESET })
export const setSessions = (payload) => ({ type: SET_SESSIONS, payload })
export const appendSessions = (payload) => ({ type: APPEND_SESSIONS, payload })
export const prependSession = (payload) => ({ type: PREPEND_SESSION, payload })
export const renameSession = (payload) => ({ type: RENAME_SESSION, payload })
export const setSessionFavorite = (payload) => ({ type: SET_SESSION_FAVORITE, payload })
export const removeSession = (payload) => ({ type: REMOVE_SESSION, payload })
export const setReadOnly = (payload) => ({ type: SET_READ_ONLY, payload })
// Boolean: true while a proposed change is awaiting the user's yes/no/other reply.
export const setAwaitingApproval = (payload) => ({ type: SET_AWAITING_APPROVAL, payload })
// Payload { question, options, multiSelect } while Kodi awaits an answer to a
// clarifying question (interactive window); null when nothing is pending.
export const setPendingInteractive = (payload) => ({ type: SET_PENDING_INTERACTIVE, payload })
export const setActiveSessionTitle = (payload) => ({ type: SET_ACTIVE_SESSION_TITLE, payload })
export const setUsagePolicies = (payload) => ({ type: SET_USAGE_POLICIES, payload })

export const initialState = {
  loading: false,
  restoring: false,
  readOnly: false,
  messages: [],
  sessions: [],
  // True when Kodi has proposed a change and is waiting on the user's reply.
  awaitingApproval: false,
  // { question, options, multiSelect } when Kodi asked a clarifying question
  // and is waiting on the user's choice; null otherwise.
  pendingInteractive: null,
  activeSessionTitle: null,
  // The user's usage-policy standings from the ws usage_update event.
  usagePolicies: [],
}

export default function kodiReducer(state = initialState, action) {
  return produce(state, (newState) => {
    const { payload } = action

    switch (action.type) {
      case ADD_MESSAGE:
        newState.messages.push({ id: payload.id || uuid(), ...payload })
        break
      case UPDATE_MESSAGE: {
        const index = newState.messages.findIndex((message) => message.id === payload.id)
        if (index !== -1) {
          newState.messages[index] = { ...newState.messages[index], ...payload }
        }
        break
      }
      case SET_LOADING:
        newState.loading = Boolean(payload)
        break
      case SET_RESTORING:
        newState.restoring = Boolean(payload)
        break
      case SET_AWAITING_APPROVAL:
        newState.awaitingApproval = Boolean(payload)
        break
      case SET_PENDING_INTERACTIVE:
        newState.pendingInteractive = payload || null
        break
      case RESET:
        // readOnly and activeSessionTitle are intentionally left alone: both
        // are set per connection from auth_success, and reset() also fires
        // mid-restore (SESSION_RESTORE_START) on the same connection — clearing
        // them there would unlock a shared read-only session after its history
        // replays, and blank the header title mid-restore. Connection teardown
        // (disconnect) / a session switch is what replaces them.
        newState.messages = []
        newState.loading = false
        newState.restoring = false
        newState.awaitingApproval = false
        newState.pendingInteractive = null
        break
      case SET_SESSIONS: {
        const incoming = payload || []
        // Preserve optimistic synthetic rows the server hasn't surfaced yet —
        // they get superseded as soon as the server returns a row with the
        // same session_id.
        const incomingIds = new Set(incoming.map((session) => session.session_id))
        const synthetic = newState.sessions.filter(
          (session) => session.synthetic && !incomingIds.has(session.session_id)
        )
        newState.sessions = [...synthetic, ...incoming]
        break
      }
      case APPEND_SESSIONS:
        newState.sessions.push(...(payload || []))
        break
      case PREPEND_SESSION: {
        if (!newState.sessions.some((session) => session.session_id === payload.session_id)) {
          newState.sessions.unshift(payload)
        }
        break
      }
      case RENAME_SESSION: {
        const index = newState.sessions.findIndex(
          (session) => session.session_id === payload.session_id
        )
        if (index !== -1) {
          newState.sessions[index].name = payload.name
        }
        break
      }
      case SET_SESSION_FAVORITE: {
        const index = newState.sessions.findIndex(
          (session) => session.session_id === payload.session_id
        )
        if (index !== -1) {
          newState.sessions[index].is_favorite = payload.is_favorite
        }
        break
      }
      case REMOVE_SESSION:
        newState.sessions = newState.sessions.filter(
          (session) => session.session_id !== payload.session_id
        )
        break
      case SET_READ_ONLY:
        newState.readOnly = Boolean(payload)
        break
      case SET_ACTIVE_SESSION_TITLE:
        newState.activeSessionTitle = payload || null
        break
      case SET_USAGE_POLICIES:
        newState.usagePolicies = payload
        break
      default:
        break
    }
  })
}
